/*
PURPOSE: Editorial ecosystem surface — the 30-day genre aggregate
rendered as a single HexBloom, plus a "Download all our data" link to the
public /trust financial-transparency ledger.
USE CASES:
- Composed inside editorial page sections (Home2 Mission, ForArtists
  ecosystem). Replaces the legacy CoralEcosystem's CoralChart visual with the
  bloom while keeping the same data source; the data-request affordance links
  to /trust rather than opening a form modal.
DOMAIN BOUNDARIES:
- Presentation only. Data comes from the shared useEcosystemData hook (single
  source of truth with the legacy EcosystemVisual). No new fetch, no fabricated copy.
- The legacy CoralEcosystem / EcosystemVisual and the other pages embedding
  them are untouched.
- Section heading and the add-to-ecosystem CtaPill belong to the host
  editorial section, not here.
ASSUMPTIONS:
- useEcosystemData returns last-30-day category aggregates ({ name, amount }),
  mapped into a CoralData whose transactionId is the fixed 'coral-ecosystem'.
- The bloom is HexBloom — a data-seeded mark: its layout seeds off that fixed
  transactionId (stable, deterministic across renders) and its cluster size
  (rings) from the category count. The base disc radius is shrunk (0.085) so
  the full cluster — up to 3 rings — scales down to fit inside the bloom frame
  without clipping. The derived CoralData is memoised so the layout is not
  recomputed each render.
- The "Download all our data" link points at the public /trust transparency
  section (the raw CSV lives there); the affordance stays available regardless
  of bloom data state.
*/

import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { trackEvent, EventName } from 'shared/lib/eventTracking';
import HexBloom from '../HexBloom/HexBloom';
import type { CoralData } from '../CoralVisualisation/CoralVisualisation';
import { useEcosystemData, GenreData } from '../../hooks/useEcosystemData';

const ECOSYSTEM_SEED = 'coral-ecosystem';

// Map the ecosystem genre aggregate into the CoralData shape CoralBloom
// consumes. Each category is one sub-bloom (genre-true colour); the ≤24 cap +
// residual fold is delegated to CoralBloom's maxSubBlooms logic so the
// behaviour lives in one place.
const ecosystemAsCoral = (data: GenreData[]): CoralData => ({
  transactionId: ECOSYSTEM_SEED,
  totalAmount: data.reduce((sum, g) => sum + g.amount, 0),
  genres: data.map((g) => ({ name: g.name, amount: g.amount })),
  artists: data.map((g) => ({ name: g.name, amount: g.amount, genre: g.name })),
});

interface EcosystemBloomProps {
  className?: string;
}

const EcosystemBloom: React.FC<EcosystemBloomProps> = ({ className }) => {
  const { data, isLoading, error } = useEcosystemData();

  const coral = useMemo(() => ecosystemAsCoral(data), [data]);

  return (
    <div className={`flex flex-col items-center text-center ${className ?? ''}`}>
      {isLoading ? (
        <div className="text-editorial-muted/70 font-cormorant text-base">Loading the coral ecosystem...</div>
      ) : error ? (
        <div className="text-editorial-muted/70 font-cormorant text-base">{error}</div>
      ) : data.length > 0 ? (
        <HexBloom coral={coral} size={360} radius={0.085} />
      ) : null}
      <p className="mt-6 text-editorial-muted/70 font-cormorant text-base">
        Live visualisation of the coral ecosystem.
      </p>
      <Link
        to="/trust#trust-financials"
        onClick={() => trackEvent(EventName.VIEW_ALL_CONTRIBUTIONS_INTERACTION, {})}
        className="mt-2 text-sm text-editorial-accent hover:underline focus:outline-none"
      >
        Download all our data
      </Link>
    </div>
  );
};

export default EcosystemBloom;
